import { useEffect, useState } from 'react'
import {
  getItemHistory, listBranches, restoreCheckpoint, hardResetCheckpoint,
  createBranch, getChanges,
} from '../../lib/assetManagerApi.js'
import { CustomAssetManager } from '../../lib/CustomAssetManager.js'
import { S, C } from './vcStyles.js'
import GraphView from './GraphView.jsx'
import FloatingWindow from '../FloatingWindow.jsx'

// ItemHistoryModal — the content-browser "Item History" view (PlayCanvas
// parity): right-click an asset → the checkpoint graph filtered to the ones
// where that one asset changed. Each node is prefixed [+]/[x]/[=]
// (added/removed/modified). Reuses GraphView's item-history mode.
//
// A WINDOW, not a modal, despite the name (kept so the import sites and the
// menu contract don't churn). It was a near-fullscreen opaque panel behind an
// opaque backdrop: measured, 1196x673 covering the entire app to show a single
// row — "Created - not yet checkpointed" — for an asset with no checkpoints
// yet. Nothing about reading an asset's history is a decision that should
// block the app, and the thing you most often want to do with it is compare it
// against the asset itself, which the backdrop hid.
//
// FloatingWindow gives it the drag / resize / pop-out-to-a-second-monitor
// chrome every other panel already has, and removes the backdrop, so the
// content browser stays visible and usable underneath. Same reasoning as
// SettingsWorkspace (Rebecca, 2026-08-06).
//
// Server is the source of truth — the version list is fetched live; failures
// surface loudly, never a faked empty graph.
//
// Right-click on a node to: Restore, Hard Reset, New Branch, View Changes.
// Restore and Hard Reset show an inline confirm bar; New Branch shows an inline
// name input. View Changes fetches and displays the changes inline.
export default function ItemHistoryModal({ characterId, section, itemId, itemLabel, onClose }) {
  const [versions, setVersions] = useState(null)
  const [branches, setBranches] = useState([])
  const [error, setError] = useState(null)
  const [busy, setBusy] = useState(false)
  const [flow, setFlow] = useState(null) // { type: 'confirm-restore|confirm-hardreset|input-branch|view-changes', ... }
  const [newBranchName, setNewBranchName] = useState('')
  const [changes, setChanges] = useState(null)

  useEffect(() => {
    let alive = true
    ;(async () => {
      try {
        const [vs, brs] = await Promise.all([
          getItemHistory(characterId, section, itemId),
          listBranches(characterId),
        ])
        if (!alive) return
        setVersions(Array.isArray(vs) ? vs : [])
        setBranches(Array.isArray(brs) ? brs : [])
      } catch (e) {
        if (alive) setError(e?.message || 'Failed to load item history')
      }
    })()
    return () => { alive = false }
  }, [characterId, section, itemId])

  // Map ItemVersion[] → checkpoint-like nodes for GraphView (id + branch_id +
  // parent_id drive layout; vcAction drives the prefix marker). Thread uncheckpointed
  // flag so GraphView can suppress the shortId chip for the creation entry.
  const nodes = (versions || []).map((v) => ({
    id: v.checkpoint_id,
    label: v.label,
    created_by: v.created_by,
    created_at: v.created_at,
    branch_id: v.branch_id,
    parent_id: v.parent_id,
    vcAction: v.action,
    uncheckpointed: v.uncheckpointed,
  }))

  // Handlers for graph actions
  const handleRestore = (node) => {
    setFlow({ type: 'confirm-restore', node })
  }

  const confirmRestore = async () => {
    setBusy(true)
    try {
      await restoreCheckpoint(characterId, flow.node.id)
      setFlow(null)
      // The restore changed server state: re-fetch the content-browser caches
      // and rebind the 3D materials so the restored data is visible without a
      // manual page reload (mirrors VersionPanel's reloadMaterials behavior).
      await CustomAssetManager.refresh()
      window.__RELOAD_MATERIALS__?.()
      // Reload versions
      const vs = await getItemHistory(characterId, section, itemId)
      setVersions(Array.isArray(vs) ? vs : [])
    } catch (e) {
      setError(e?.message || 'Restore failed')
    } finally {
      setBusy(false)
    }
  }

  const handleHardReset = (node) => {
    setFlow({ type: 'confirm-hardreset', node })
  }

  const confirmHardReset = async () => {
    setBusy(true)
    try {
      await hardResetCheckpoint(characterId, flow.node.id)
      setFlow(null)
      await CustomAssetManager.refresh()
      window.__RELOAD_MATERIALS__?.()
      // Reload versions
      const vs = await getItemHistory(characterId, section, itemId)
      setVersions(Array.isArray(vs) ? vs : [])
    } catch (e) {
      setError(e?.message || 'Hard reset failed')
    } finally {
      setBusy(false)
    }
  }

  const handleNewBranch = (node) => {
    setNewBranchName('')
    setFlow({ type: 'input-branch', node })
  }

  const confirmNewBranch = async () => {
    if (!newBranchName.trim()) return
    setBusy(true)
    try {
      await createBranch(characterId, { name: newBranchName, source_checkpoint_id: flow.node.id })
      setFlow(null)
      setNewBranchName('')
      // Reload versions
      const vs = await getItemHistory(characterId, section, itemId)
      setVersions(Array.isArray(vs) ? vs : [])
    } catch (e) {
      setError(e?.message || 'Create branch failed')
    } finally {
      setBusy(false)
    }
  }

  const handleViewChanges = async (node) => {
    setFlow({ type: 'view-changes', node, data: null })
    setBusy(true)
    try {
      const ch = await getChanges(characterId, node.id)
      setFlow((f) => (f?.type === 'view-changes' ? { ...f, data: Array.isArray(ch) ? ch : [] } : f))
    } catch (e) {
      setError(e?.message || 'Failed to load changes')
    } finally {
      setBusy(false)
    }
  }

  return (
    <FloatingWindow
      id="item-history"
      title="ITEM HISTORY"
      onClose={onClose}
      dockable={false}
      defaultSize={{ w: 900, h: 560 }}
      barExtra={<span style={S.dim}>{itemLabel || itemId} · {section}</span>}
    >
      <div style={{ ...S.graphCanvas, flex: 1, minHeight: 0 }}>
          {error && <div style={{ color: C.danger, padding: 20 }}>⚠ {error}</div>}
          {!error && versions === null && <div style={{ color: C.textDim, padding: 20 }}>Loading…</div>}
          {!error && versions !== null && versions.length === 0 && (
            <div style={{ color: C.textDim, padding: 20 }}>No checkpointed history for this asset yet.</div>
          )}
          {!error && nodes.length > 0 && (
            <GraphView
              checkpoints={nodes}
              branches={branches}
              itemHistory
              selectedId={null}
              onSelect={() => {}}
              onRestore={handleRestore}
              onHardReset={handleHardReset}
              onNewBranch={handleNewBranch}
              onViewChanges={handleViewChanges}
            />
          )}

          {/* Inline confirm bar for Restore */}
          {flow?.type === 'confirm-restore' && (
            <div style={{ position: 'absolute', bottom: 12, left: 12, right: 12, background: C.bgRow, border: `1px solid ${C.borderInput}`, borderRadius: 4, padding: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ flex: 1, fontSize: 12 }}>Restore this checkpoint?</span>
              <button style={{ ...S.ghost }} disabled={busy} onClick={() => setFlow(null)}>Cancel</button>
              <button style={{ ...S.primary }} disabled={busy} onClick={confirmRestore}>{busy ? 'Restoring…' : 'Confirm'}</button>
            </div>
          )}

          {/* Inline confirm bar for Hard Reset */}
          {flow?.type === 'confirm-hardreset' && (
            <div style={{ position: 'absolute', bottom: 12, left: 12, right: 12, background: C.bgRow, border: `1px solid ${C.borderInput}`, borderRadius: 4, padding: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ flex: 1, fontSize: 12 }}>Hard reset to this checkpoint?</span>
              <button style={{ ...S.ghost }} disabled={busy} onClick={() => setFlow(null)}>Cancel</button>
              <button style={{ ...S.danger }} disabled={busy} onClick={confirmHardReset}>{busy ? 'Resetting…' : 'Confirm'}</button>
            </div>
          )}

          {/* Inline name input for New Branch */}
          {flow?.type === 'input-branch' && (
            <div style={{ position: 'absolute', bottom: 12, left: 12, right: 12, background: C.bgRow, border: `1px solid ${C.borderInput}`, borderRadius: 4, padding: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                type="text"
                placeholder="Branch name"
                value={newBranchName}
                onChange={(e) => setNewBranchName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') confirmNewBranch()
                  if (e.key === 'Escape') setFlow(null)
                }}
                style={{ ...S.input, flex: 1 }}
                autoFocus
                disabled={busy}
              />
              <button style={{ ...S.ghost }} disabled={busy} onClick={() => setFlow(null)}>Cancel</button>
              <button style={{ ...S.primary }} disabled={busy || !newBranchName.trim()} onClick={confirmNewBranch}>{busy ? 'Creating…' : 'Create'}</button>
            </div>
          )}

          {/* Changes list */}
          {flow?.type === 'view-changes' && (
            <div style={{ position: 'absolute', bottom: 12, left: 12, right: 12, background: C.bgRow, border: `1px solid ${C.borderInput}`, borderRadius: 4, padding: 10, maxHeight: 200, overflow: 'auto' }}>
              <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}>Changes in {flow.node.id.substring(0, 8)}</div>
              {flow.data?.length === 0 ? (
                <div style={{ fontSize: 11, color: C.textDim }}>No changes</div>
              ) : (
                <div style={{ fontSize: 11 }}>
                  {flow.data?.map((ch, i) => (
                    <div key={i} style={{ marginBottom: 4, color: C.textDim }}>
                      {ch}
                    </div>
                  ))}
                </div>
              )}
              <button style={{ ...S.ghost, marginTop: 8 }} onClick={() => setFlow(null)}>Close</button>
            </div>
          )}
      </div>
    </FloatingWindow>
  )
}
